import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction, ForwardedRef } from "react";
import clsx from "clsx";
import { useDispatch, useSelector, useStore } from "react-redux";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import CallSplitIcon from "@material-ui/icons/CallSplit";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles, type Theme } from "@material-ui/core/styles";

import type { Transaction, TransactionFormError } from "src/interfaces";
import type { GeneralLedgerAccountV2 } from "src/interfaces/generalLedgerAccount.interface";
import Autocomplete from "src/components/Autocomplete";
import VendorField from "src/components/VendorField";
import SaleAutocompleteField from "src/components/SaleAutocompleteField";
import Tooltip from "src/components/Tooltip";
import { AddGeneralLedgerAccountDialog } from "src/pages/RootDialogs/AddGeneralLedgerAccountDialog";
import { generalLedgerAccountsSelector } from "src/store/glAccounts/selector";
import { linkedItemsSelector } from "src/store/transaction/selector";
import { setActiveDialog } from "src/store/adminHtml/actions";
import {
  generalLedgerAccountTypeToLabel,
  isBankTransfer,
  GeneralLedgerAccountTypeOrderById,
  TransfersAccountId,
} from "src/utils/general-ledger-accounts";

import { Row } from "./Row";
import { DateInput } from "./DateInput";
import { DefaultForm } from "./DefaultForm";
import { MileageForm } from "./MileageForm";
import { InventoryForm } from "./InventoryForm";
import { TransactionTypeField } from "./TransactionTypeField";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  containerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(1),
    "& .transaction-type": {
      order: "-2",
    },
    "& .date": {
      order: "-1",
    },
  },
  option: {
    padding: "0 !important",
  },
  optionContent: {
    padding: theme.spacing(0.75, 3),
    width: "100%",
  },
  optionContentDisabled: {
    opacity: 0.6,
  },
}));

export interface SplitToggleButtonProps {
  disabled?: boolean;
  active: boolean;
  onClick: () => void;
  title: string;
}

/** The split/unsplit toggle, shared between single and split bodies. */
export function SplitToggleButton({
  disabled,
  active,
  onClick,
  title,
}: SplitToggleButtonProps) {
  return (
    <Box display="flex" alignItems="center" ml={1}>
      <Tooltip title={title}>
        <span>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={onClick}
            color={active ? "primary" : "default"}
            aria-label="Split Transaction"
          >
            <CallSplitIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

export interface SingleLineBodyProps {
  values: Partial<Transaction>;
  setValues: Dispatch<SetStateAction<Partial<Transaction>>>;
  errors: TransactionFormError;
  loading: boolean;
  handleChange: (
    prop: keyof Transaction
  ) => (event: { target: { value?: any } }) => void;
  dateRef: ForwardedRef<HTMLDivElement>;
  isMoneyIn: boolean;
  isMileage: boolean;
  isInventory: boolean;
  /** Currently selected GL account (resolved by the parent). */
  account?: GeneralLedgerAccountV2;
  /** Name of the account the mapped transfer counterparty uses, if any. */
  mappedTransactionAccount?: string;
  accountName?: string;
  accountOpen: boolean;
  setAccountOpen: (open: boolean) => void;
  tourOpen: boolean;
  onEnterBalanceTransfer: () => void;
  onRequestNonInventoryConfirm: (values: Partial<Transaction>) => void;
  splitToggleDisabled: boolean;
  splitToggleTitle: string;
  onToggleSplit: () => void;
}

export function SingleLineBody({
  values,
  setValues,
  errors,
  loading,
  handleChange,
  dateRef,
  isMoneyIn,
  isMileage,
  isInventory,
  account,
  mappedTransactionAccount,
  accountName,
  accountOpen,
  setAccountOpen,
  tourOpen,
  onEnterBalanceTransfer,
  onRequestNonInventoryConfirm,
  splitToggleDisabled,
  splitToggleTitle,
  onToggleSplit,
}: SingleLineBodyProps) {
  const classes = useStyles();
  const store = useStore();
  const dispatch = useDispatch();
  const generalLedgerAccounts = useSelector(generalLedgerAccountsSelector);
  const accountId = account?.id;
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  // For inventory accounts the Date field is rendered conditionally (only once
  // `isInventory` becomes true), so it isn't mounted yet when an inventory
  // account is selected. Defer focusing it until the field mounts.
  const focusDateRef = useRef(false);

  useEffect(() => {
    if (isInventory && focusDateRef.current) {
      focusDateRef.current = false;
      (dateRef as any)?.current?.focus?.();
    }
  }, [isInventory, dateRef]);

  // Apply a chosen GL account to the form, including the inventory-linking and
  // non-inventory confirm side effects. Shared by direct selection and by the
  // "Add New Account" flow so a freshly created account autofills the field.
  const selectAccount = (value: any) => {
    if (value?.id === TransfersAccountId) {
      onEnterBalanceTransfer();
      setAccountOpen(false);
      return;
    }

    if (value?.id) {
      const newValues = {
        ...values,
        gl_account_id: value.id,
        account: value.id === "transfers" ? "Transfers" : value.name,
      };

      const selectedInventory = value?.type?.[1] === "inventory";

      if (selectedInventory) {
        focusDateRef.current = true;
        (dateRef as any)?.current?.focus?.();
        if (values.date) {
          const items = linkedItemsSelector(store.getState(), {
            date: values.date,
          });
          newValues.items = items.map(({ type, item }) => ({
            type,
            id: item.id,
          }));
        } else {
          newValues.items = [];
        }
      } else {
        if (isInventory) {
          onRequestNonInventoryConfirm({
            gl_account_id: value.id,
            account: value.id === "transfers" ? "Transfers" : value.name,
          });
          setAccountOpen(false);
          return;
        }

        delete newValues.items;
      }

      setValues(newValues);
      setAccountOpen(false);
    }
  };

  const options = useMemo(() => {
    const accounts = [...generalLedgerAccounts].filter((e) => {
      if (!e) return false;
      if (e.system && e.id !== "sales-tax-payable") return false;
      if (accountId === "transfers" && e.name === mappedTransactionAccount)
        return false;
      return true;
    });

    accounts.sort(
      (a, b) =>
        GeneralLedgerAccountTypeOrderById[a.type[0]] -
          GeneralLedgerAccountTypeOrderById[b.type[0]] ||
        a.name.localeCompare(b.name)
    );

    if (accountId === "transfers") {
      accounts.unshift({
        name: mappedTransactionAccount,
        id: accountId,
        type: [],
      } as any);
    } else if (!accountId && accountName) {
      accounts.unshift({
        name: accountName,
        type: [],
      } as any);
    }

    accounts.unshift({
      name: "Add New Account",
      type: ["new"],
      id: "add_new_account",
    } as any);

    accounts.unshift({
      name: "Balance Transfer",
      type: ["balance-transfer"],
      id: TransfersAccountId,
    } as any);

    return accounts.filter((a) => !a.deleted || a.id === accountId);
  }, [generalLedgerAccounts, accountName, accountId, mappedTransactionAccount]);

  const Form = isMileage ? MileageForm : DefaultForm;
  const isUpdate = !!values.id;

  return (
    <>
      <AddGeneralLedgerAccountDialog
        open={addAccountOpen}
        onClose={(newAccount) => {
          setAddAccountOpen(false);
          if (newAccount) selectAccount(newAccount);
        }}
      />
      <div className={isInventory ? classes.containerGrid : classes.container}>
        <TransactionTypeField
          isMoneyIn={isMoneyIn}
          loading={loading}
          isUpdate={isUpdate}
          onDirectionChange={(nextMoneyIn) => {
            const multiplier = nextMoneyIn ? -1 : 1;
            setValues((v) => ({
              ...v,
              amount: Math.abs(v?.amount || 0) * multiplier,
            }));
          }}
          onJournalEntry={() => dispatch(setActiveDialog("add_journal"))}
        />
        <Row
          text="The category of the transaction."
          InfoTooltipProps={{
            id: "transaction-edit-dialog-general-ledger-account-tooltip",
          }}
          id={"general-ledger-row"}
        >
          <Autocomplete
            onOpen={() => setAccountOpen(true)}
            fullWidth
            disabled={loading}
            options={options}
            classes={{
              option: classes.option,
            }}
            value={(values.gl_account_id || values.account || null) as any}
            onChange={(_e, value) => {
              if (value?.type && isBankTransfer(value.type)) return;

              if (value?.name === "Add New Account") {
                setAddAccountOpen(true);
                return;
              }

              selectAccount(value);
            }}
            autoHighlight
            renderInput={(params) => (
              <TextField
                {...params}
                error={errors.account ? true : false}
                helperText={errors.account}
                margin="dense"
                required
                label="General Ledger Account"
                fullWidth
                variant="outlined"
                autoFocus
                onBlur={() => {
                  if (!tourOpen) {
                    setAccountOpen(false);
                  }
                }}
              />
            )}
            getOptionLabel={(option) =>
              option?.name ||
              options.find((opt) => opt.id === option)?.name ||
              options.find((opt) => opt.name === option)?.name
            }
            renderOption={(option, { inputValue }) => {
              const disabled = isBankTransfer(option.type);
              const matches = match(option.name, inputValue);
              const parts = parse(option.name, matches);
              const content = (
                <div
                  className={clsx(
                    classes.optionContent,
                    disabled && classes.optionContentDisabled
                  )}
                  {...(disabled && {
                    onClick: (e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                    },
                  })}
                >
                  {parts.map((part, index) => (
                    <span
                      id={option.id}
                      key={index}
                      style={{
                        fontWeight: part.highlight ? 700 : 400,
                      }}
                    >
                      {part.text}
                    </span>
                  ))}
                </div>
              );
              return disabled ? (
                <Tooltip title="To transfer between accounts, select Balance Transfer.">
                  {content}
                </Tooltip>
              ) : (
                content
              );
            }}
            groupBy={(option) =>
              option.type?.[0] === "balance-transfer" ||
              option.type?.[0] === "new"
                ? ""
                : generalLedgerAccountTypeToLabel(option.type)[0]
            }
            getOptionSelected={(option, value) =>
              option.id === value || option.name === value
            }
            open={accountOpen}
          />
          <SplitToggleButton
            disabled={loading || splitToggleDisabled}
            active={false}
            onClick={onToggleSplit}
            title={splitToggleTitle}
          />
        </Row>
        {isInventory ? (
          <Row className="date">
            <DateInput
              ref={dateRef}
              value={values.date}
              onChange={(event) => {
                let value = event.target.value;
                if (!isUpdate && value?.getTime()) {
                  value.setUTCHours(12, 0, 0, 0);
                }

                setValues((values) => {
                  const newValues = { ...values, date: value };
                  if (Array.isArray(newValues.items)) {
                    if (newValues.items.length === 0 || !newValues.id) {
                      const items = linkedItemsSelector(store.getState(), {
                        date: value,
                      });
                      newValues.items = items.map(({ type, item }) => ({
                        type,
                        id: item.id,
                      }));
                    }
                  }

                  return newValues;
                });
              }}
              loading={loading}
              error={!!errors.date}
              helperText={errors.date}
            />
          </Row>
        ) : null}
        <Row text="Where the purchase was made.">
          <VendorField
            margin="dense"
            disabled={loading}
            value={values.vendor}
            onChange={(vendor) => {
              setValues((v) => ({ ...v, vendor }));
            }}
          />
        </Row>
        <Form
          values={values}
          errors={errors}
          handleChange={handleChange}
          loading={loading}
          setValues={setValues}
          dateRef={dateRef}
          showDate={!isInventory}
        />
        {account?.type?.[0] === "expenses" ? (
          <Row text="Link this expense to a sale by attaching a sale here.">
            <SaleAutocompleteField
              value={values.orderId}
              onChange={(sale) => {
                const transactionId = sale?.transaction_id || sale?.id;
                setValues((v) => {
                  const values = { ...v };
                  if (transactionId) values.orderId = transactionId;
                  else (values as any).orderId = null;
                  return values;
                });
              }}
              disabled={loading}
            />
          </Row>
        ) : null}
      </div>
      {isInventory ? (
        <InventoryForm
          values={values}
          errors={errors}
          loading={loading}
          setValues={setValues}
        />
      ) : null}
    </>
  );
}
